import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { format } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import useAuthorize from '../hooks/useAuthorize';
import { DASHBOARD_MODULE_ROLES } from '../roles/portalRoles';
import SellerAcquisitionFunnelChart from './SellerAcquisitionFunnelChart';
import {
  fetchSellerAcquisitionCampaigns,
  SellerAcquisitionCampaignRow,
} from './sellerAcquisitionApi';

const MARKET_AD_URL =
  'https://credcosourcing.com/market?utm_source=facebook&utm_medium=paid_social&utm_campaign=Site_Market_Seller_Leads&utm_content=Seller_warehouse_Ad&utm_term=Business_Related_India';

const pct = (num: number, den: number) => {
  if (!den) return '0%';
  return `${((num / den) * 100).toFixed(1)}%`;
};

const DashboardSellerAcquisition = () => {
  useAuthorize(DASHBOARD_MODULE_ROLES);

  const getDefaultFromDate = () => format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<SellerAcquisitionCampaignRow[]>([]);
  const [totals, setTotals] = useState({
    market_landings: 0,
    seller_register_clicks: 0,
    seller_register_starts: 0,
    seller_register_step2: 0,
    registration_requests: 0,
    sellers_created: 0,
  });
  const [selected, setSelected] = useState<SellerAcquisitionCampaignRow | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSellerAcquisitionCampaigns(fromDate, toDate);
      setCampaigns(data.campaigns || []);
      setTotals(data.totals || totals);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load seller acquisition funnel';
      setError(message);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [fromDate, toDate]);

  const funnelTotals = useMemo(() => {
    if (selected) {
      return {
        market_landings: selected.market_landings,
        seller_register_clicks: selected.seller_register_clicks,
        seller_register_starts: selected.seller_register_starts,
        seller_register_step2: selected.seller_register_step2,
        sellers_created: selected.sellers_created,
      };
    }
    return {
      market_landings: totals.market_landings,
      seller_register_clicks: totals.seller_register_clicks,
      seller_register_starts: totals.seller_register_starts,
      seller_register_step2: totals.seller_register_step2,
      sellers_created: totals.sellers_created,
    };
  }, [selected, totals]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <StorefrontIcon color="primary" />
        <Typography variant="h5">Marketplace seller acquisition</Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Paid social → <strong>/market</strong> → Register as seller → step 2 listing wizard. Events are
        stored in audit <code>campaign_attribution_events</code>; confirmed sellers also come from{' '}
        <code>seller_registration_requests</code> with matching UTM.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Facebook ad URL:{' '}
        <Box component="code" sx={{ display: 'block', mt: 1, wordBreak: 'break-all', fontSize: 12 }}>
          {MARKET_AD_URL}
        </Box>
        <Link component={RouterLink} to="/dashboard/social" sx={{ display: 'inline-block', mt: 1 }}>
          App download social campaigns →
        </Link>
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label="From"
            type="date"
            fullWidth
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label="To"
            type="date"
            fullWidth
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              ['Market landings', totals.market_landings],
              ['Register clicks', totals.seller_register_clicks],
              ['Registrations started', totals.seller_register_starts],
              ['Step 2 reached', totals.seller_register_step2],
              ['Sellers created', totals.sellers_created],
            ].map(([label, value]) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={String(label)}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="h6">{value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Campaign</TableCell>
                      <TableCell align="right">Market</TableCell>
                      <TableCell align="right">Clicks</TableCell>
                      <TableCell align="right">Started</TableCell>
                      <TableCell align="right">Step 2</TableCell>
                      <TableCell align="right">Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          No marketplace seller funnel events in this range. Send traffic with UTM parameters
                          (e.g. <strong>Site_Market_Seller_Leads</strong>) to credcosourcing.com/market.
                        </TableCell>
                      </TableRow>
                    ) : (
                      campaigns.map((row) => (
                        <TableRow
                          key={`${row.utm_campaign}-${row.utm_source}`}
                          hover
                          selected={selected?.utm_campaign === row.utm_campaign}
                          onClick={() => setSelected(row)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {row.utm_campaign}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.utm_source} / {row.utm_medium}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{row.market_landings}</TableCell>
                          <TableCell align="right">
                            {row.seller_register_clicks} ({pct(row.seller_register_clicks, row.market_landings)})
                          </TableCell>
                          <TableCell align="right">{row.seller_register_starts}</TableCell>
                          <TableCell align="right">
                            {row.seller_register_step2} ({pct(row.seller_register_step2, row.seller_register_clicks)})
                          </TableCell>
                          <TableCell align="right">{row.sellers_created}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {selected
                      ? `Funnel — ${selected.utm_campaign}`
                      : 'Portfolio funnel'}
                  </Typography>
                  <SellerAcquisitionFunnelChart totals={funnelTotals} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default DashboardSellerAcquisition;
